import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Coins, DollarSign, Store, ShieldCheck, Landmark, ArrowDownToLine, Lock, Fingerprint, MapPin, History, Smartphone, KeyRound, Gift, Users, Target } from "lucide-react";
import { loadStripe } from "@stripe/stripe-js";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/lib/i18n";

const WD_STATUS = {
  aml_review: { label: "In Review", pct: 40, bar: "bg-amber-500", text: "text-amber-300" },
  pending: { label: "Pending Approval", pct: 65, bar: "bg-cyan-500", text: "text-cyan-300" },
  approved: { label: "Approved", pct: 85, bar: "bg-emerald-500", text: "text-emerald-300" },
  paid: { label: "Paid", pct: 100, bar: "bg-emerald-500", text: "text-emerald-300" },
  rejected: { label: "Rejected · Refunded", pct: 100, bar: "bg-red-500", text: "text-red-300" },
};

const PREVIEW_PACKS = [
  { lookup_key: "coins_1000", name: "Starter Stack", amount: 99, coins: 1000 },
  { lookup_key: "coins_5500", name: "Pro Pile", amount: 499, coins: 5500 },
  { lookup_key: "coins_12000", name: "Elite Vault", amount: 999, coins: 12000 },
];
const COIN_TERMS = "Fun Coins are for entertainment only. They can never be exchanged for money, transferred, or redeemed for money or prizes.";

export default function Wallet({ initialTab }) {
  const { user, refresh } = useAuth();
  const { t } = useT();
  const [catalog, setCatalog] = useState({ coin_packs: [], cash_deposits: [] });
  const [ledger, setLedger] = useState({ coin_ledger: [], cash_ledger: [] });
  const storeOn = !!user?.coin_store_enabled;
  const [tab, setTab] = useState(initialTab || (storeOn ? "coins" : "free"));
  const [kyc, setKyc] = useState({ full_name: "", doc_type: "passport", doc_number: "" });
  const [wd, setWd] = useState({ amount: "", method: "ach", destination: "" });
  const [verifying, setVerifying] = useState(false);
  const [showManual, setShowManual] = useState(false);
  const [geoInfo, setGeoInfo] = useState(null);
  const [payout, setPayout] = useState(null);
  const [withdrawals, setWithdrawals] = useState([]);
  const [smsOn, setSmsOn] = useState(false);
  const [phoneInput, setPhoneInput] = useState(user?.pending_phone || "");
  const [smsConsent, setSmsConsent] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otpCode, setOtpCode] = useState("");
  const [smsBusy, setSmsBusy] = useState(false);

  const loadSms = async () => {
    try { const { data } = await api.get("/auth/sms/status"); setSmsOn(!!data.enabled); } catch { /* ignore */ }
  };
  const sendPhoneOtp = async () => {
    setSmsBusy(true);
    try {
      await api.post("/auth/phone/send-otp", { phone: phoneInput });
      setOtpSent(true);
      toast.success("Verification code sent by SMS");
    } catch (e) { toast.error(apiErr(e)); }
    finally { setSmsBusy(false); }
  };
  const verifyPhoneOtp = async () => {
    setSmsBusy(true);
    try {
      await api.post("/auth/phone/verify", { code: otpCode });
      await refresh();
      setOtpSent(false); setOtpCode(""); setPhoneInput("");
      toast.success("Phone verified");
    } catch (e) { toast.error(apiErr(e)); }
    finally { setSmsBusy(false); }
  };
  const toggle2fa = async (enabled) => {
    try {
      await api.post("/auth/2fa/toggle", { enabled });
      await refresh();
      toast.success(enabled ? "Two-factor login enabled" : "Two-factor login disabled");
    } catch (e) { toast.error(apiErr(e)); }
  };

  const loadWithdrawals = async () => {
    try { const { data } = await api.get("/account/withdrawals"); setWithdrawals(data); } catch { /* ignore */ }
  };
  const load = async () => {
    try {
      const [c, w, p] = await Promise.all([
        api.get("/payments/catalog"), api.get("/account/wallet"),
        api.get("/account/payout/status").catch(() => ({ data: null })),
      ]);
      setCatalog(c.data); setLedger(w.data); setPayout(p.data);
    } catch (e) { toast.error(apiErr(e)); }
  };
  const setupPayout = async () => {
    try {
      const { data } = await api.post("/account/payout/connect", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) { toast.error(apiErr(e)); }
  };
  const refreshGeo = async () => {
    try { const { data } = await api.post("/account/geo-refresh"); setGeoInfo(data); await refresh(); } catch { /* best effort */ }
  };
  useEffect(() => { load(); refreshGeo(); loadWithdrawals(); loadSms(); }, []);
  useEffect(() => {
    if (tab !== "cash") return;
    loadWithdrawals();
    const id = setInterval(loadWithdrawals, 15000);
    return () => clearInterval(id);
  }, [tab]);

  const buy = async (lookup_key) => {
    try {
      const { data } = await api.post("/payments/checkout", { lookup_key, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(apiErr(e)); }
  };

  const verifyIdentity = async () => {
    setVerifying(true);
    try {
      const { data: cfg } = await api.get("/account/kyc/config");
      if (!cfg.publishable_key) throw new Error("Verification is not configured yet.");
      const { data: session } = await api.post("/account/kyc/identity/start", { origin_url: window.location.origin });
      const stripe = await loadStripe(cfg.publishable_key);
      const { error } = await stripe.verifyIdentity(session.client_secret);
      if (error) throw new Error(error.message);
      toast.success("Submitted — checking verification status…");
      for (let i = 0; i < 20; i++) {
        const { data: st } = await api.get(`/account/kyc/identity/status/${session.id}`);
        if (st.kyc_status === "approved") { toast.success("Identity approved!"); break; }
        if (["needs_input", "canceled"].includes(st.kyc_status)) { toast.error("Verification incomplete — please try again."); break; }
        await new Promise((r) => setTimeout(r, 2000));
      }
      await refresh();
    } catch (e) { toast.error(e.message || apiErr(e)); }
    finally { setVerifying(false); }
  };

  const submitKyc = async () => {
    try {
      await api.post("/account/kyc/submit", kyc);
      await refresh();
      toast.success("KYC submitted — pending admin review");
    } catch (e) { toast.error(apiErr(e)); }
  };

  const withdraw = async () => {
    try {
      const { data } = await api.post("/account/withdraw", { amount: parseFloat(wd.amount), method: wd.method, destination: wd.destination });
      await refresh(); load(); loadWithdrawals();
      if (data.aml_review) toast.success(`Withdrawal of $${wd.amount} submitted — held for compliance review`);
      else toast.success(`Withdrawal of $${wd.amount} requested — pending approval`);
      setWd({ amount: "", method: "ach", destination: "" });
    } catch (e) { toast.error(apiErr(e)); }
  };

  const kycStatus = user?.kyc_status;
  const cashOn = !!user?.cash_enabled;

  return (
    <div className="p-4 lg:p-8">
      <h1 className="font-display text-3xl sm:text-4xl font-black mb-2">{t("your_wallet")}</h1>
      <p className="text-gray-400 mb-6">{t("wallet_sub")}</p>

      {/* Balances */}
      <div className={`grid grid-cols-1 ${cashOn ? "sm:grid-cols-2" : ""} gap-6 mb-8`}>
        <div data-testid="coins-balance-card" className="glass rounded-2xl p-6 border border-amber-500/25 glow-coins">
          <div className="flex items-center gap-2 text-amber-400 mb-1"><Coins className="w-5 h-5" /><span className="text-sm font-semibold uppercase tracking-wide">Fun Coins</span></div>
          <p className="font-mono font-black text-4xl text-amber-300">{(user?.coins ?? 0).toLocaleString()}</p>
          <p className="text-xs text-gray-500 mt-1">Non-redeemable · no monetary value · entertainment only</p>
        </div>
        {cashOn && (
          <div data-testid="cash-balance-card" className="glass rounded-2xl p-6 border border-emerald-500/25 glow-cash">
            <div className="flex items-center gap-2 text-emerald-400 mb-1"><DollarSign className="w-5 h-5" /><span className="text-sm font-semibold uppercase tracking-wide">Cash Balance</span></div>
            <p className="font-mono font-black text-4xl text-emerald-300">${(user?.cash ?? 0).toFixed(2)}</p>
            <p className="text-xs text-gray-500 mt-1">Skill winnings · withdrawable with KYC</p>
          </div>
        )}
      </div>

      {/* Account security: phone verification + 2FA */}
      <div data-testid="account-security-card" className="glass rounded-2xl p-6 border border-cyan-500/20 mb-8">
        <h3 className="font-display text-xl font-bold mb-1 flex items-center gap-2">
          <Smartphone className="w-5 h-5 text-cyan-400" /> Phone &amp; Two-Factor Security
        </h3>
        <p data-testid="sms-purpose-copy" className="text-xs text-gray-500 mb-4">Verify your mobile number to receive one-time login codes and account security alerts, and to enable two-factor login. SMS is never used for marketing.</p>

        {user?.phone_verified ? (
          <div className="space-y-4">
            <p data-testid="phone-verified-status" className="text-sm text-emerald-300 flex items-center gap-2">
              <ShieldCheck className="w-4 h-4" /> Verified · {user?.phone ? `•••• ${String(user.phone).slice(-4)}` : "on file"}
            </p>
            <div className="flex items-center justify-between p-4 rounded-xl bg-black/30 border border-white/10">
              <div className="flex items-center gap-3">
                <KeyRound className="w-5 h-5 text-cyan-400" />
                <div>
                  <p className="font-semibold text-sm">Two-factor login</p>
                  <p className="text-xs text-gray-500">Require an SMS code every time you sign in.</p>
                </div>
              </div>
              <button data-testid="twofa-toggle-button" onClick={() => toggle2fa(!user?.two_factor_enabled)} disabled={!smsOn}
                className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors duration-200 disabled:opacity-40 ${user?.two_factor_enabled ? "bg-emerald-500/20 border border-emerald-500/40 text-emerald-300" : "bg-white/5 border border-white/10 text-gray-300 hover:bg-white/10"}`}>
                {user?.two_factor_enabled ? "Enabled" : "Enable"}
              </button>
            </div>
          </div>
        ) : !smsOn ? (
          <p data-testid="sms-not-configured" className="text-sm text-amber-300 flex items-center gap-2">
            <Lock className="w-4 h-4" /> {t("sms_coming_soon")}
          </p>
        ) : !otpSent ? (
          <div className="max-w-md space-y-3">
            <input data-testid="phone-input" type="tel" placeholder="+1" autoComplete="tel" value={phoneInput}
              onChange={(e) => setPhoneInput(e.target.value)}
              className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2.5 outline-none focus:border-cyan-500 transition-colors duration-200 font-mono" />
            <label data-testid="wallet-sms-consent" className="flex items-start gap-2.5 text-[11px] leading-relaxed text-gray-400 cursor-pointer">
              <input data-testid="wallet-sms-consent-checkbox" type="checkbox" checked={smsConsent} onChange={(e) => setSmsConsent(e.target.checked)}
                className="mt-0.5 h-4 w-4 shrink-0 rounded border-white/20 bg-black/40 accent-cyan-500" />
              <span>{t("sms_consent")}{" "}
                <Link to="/terms" className="text-cyan-400 hover:underline">{t("nav_terms")}</Link> · <Link to="/privacy" className="text-cyan-400 hover:underline">{t("nav_privacy")}</Link>
              </span>
            </label>
            <button data-testid="phone-send-otp-button" onClick={sendPhoneOtp} disabled={smsBusy || !phoneInput || !smsConsent}
              className="w-full sm:w-auto px-5 py-2.5 rounded-xl bg-gradient-to-r from-cyan-500 to-emerald-600 font-semibold hover:opacity-90 transition-opacity duration-200 disabled:opacity-40 whitespace-nowrap">
              {smsBusy ? "Sending…" : "Send code"}
            </button>
          </div>
        ) : (
          <div className="flex flex-col sm:flex-row gap-3 max-w-md">
            <input data-testid="phone-otp-input" inputMode="numeric" placeholder="6-digit code" value={otpCode}
              onChange={(e) => setOtpCode(e.target.value)}
              className="flex-1 bg-black/40 border border-white/10 rounded-xl px-3 py-2.5 outline-none focus:border-cyan-500 transition-colors duration-200 font-mono tracking-widest" />
            <button data-testid="phone-verify-otp-button" onClick={verifyPhoneOtp} disabled={smsBusy || !otpCode}
              className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-cyan-500 to-emerald-600 font-semibold hover:opacity-90 transition-opacity duration-200 disabled:opacity-40 whitespace-nowrap">
              {smsBusy ? "Verifying…" : "Verify"}
            </button>
            <button data-testid="phone-otp-cancel-button" onClick={() => { setOtpSent(false); setOtpCode(""); }}
              className="px-3 py-2.5 rounded-xl bg-white/5 border border-white/10 text-sm text-gray-400 hover:text-white transition-colors duration-200">
              Cancel
            </button>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-2 p-1 rounded-xl bg-black/40 w-fit mb-6">
        {[...(storeOn ? [] : [["free", "Free Coins"]]), ["coins", "Coin Store"], ...(cashOn ? [["cash", "Cash & Withdraw"]] : []), ["history", "History"]].map(([k, l]) => (
          <button key={k} data-testid={`wallet-tab-${k}`} onClick={() => setTab(k)}
            className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors duration-200 ${tab === k ? "bg-white/10 text-white" : "text-gray-400 hover:text-white"}`}>{l}</button>
        ))}
      </div>

      {tab === "free" && (
        <div data-testid="free-coins-panel" className="glass rounded-2xl p-6 border border-amber-500/20">
          <h3 className="font-display text-xl font-bold mb-1 flex items-center gap-2"><Gift className="w-5 h-5 text-amber-400" /> Coins are free right now</h3>
          <p className="text-sm text-gray-400 mb-5">PhonePlay is free to play. Earn Fun Coins every day and climb the leaderboards — coins have no monetary value and are never sold for prizes.</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {[[Gift, "Daily bonus", "Claim free coins every day on the Play page — streaks pay more."],
              [Target, "Daily quests", "Finish quests for bonus coins and VIP points."],
              [Users, "Invite friends", "Earn 2,500 coins when a friend signs up and plays."]].map(([Icon, title, desc]) => (
              <div key={title} className="p-4 rounded-xl bg-black/30 border border-white/10">
                <Icon className="w-5 h-5 text-amber-300 mb-2" />
                <p className="font-semibold text-sm">{title}</p>
                <p className="text-xs text-gray-500 mt-1">{desc}</p>
              </div>
            ))}
          </div>
          <div className="flex flex-wrap gap-3 mt-5">
            <Link to="/casino" data-testid="free-coins-daily-link" className="px-4 py-2 rounded-xl bg-gradient-to-r from-amber-500 to-purple-600 text-sm font-semibold">Claim daily bonus</Link>
            <Link to="/invite" data-testid="free-coins-invite-link" className="px-4 py-2 rounded-xl bg-white/5 border border-white/10 text-sm">Invite friends</Link>
          </div>
        </div>
      )}

      {tab === "coins" && (
        <div data-testid="coin-store-modal" className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {!storeOn && (
            <div data-testid="coin-store-coming-soon" className="sm:col-span-3 glass rounded-2xl p-4 border border-amber-500/20 text-center">
              <p className="font-semibold text-amber-300">Coin Store — coming soon</p>
              <p className="text-xs text-gray-400 mt-1">Coin packs aren't on sale yet. Until then, coins are free: claim the daily bonus, finish quests and invite friends.</p>
            </div>
          )}
          {(storeOn ? catalog.coin_packs : PREVIEW_PACKS).map((p) => (
            <div key={p.lookup_key} className="glass rounded-2xl p-6 border border-amber-500/20 hover:border-amber-500/50 transition-colors duration-200 text-center">
              <Coins className="w-10 h-10 text-amber-400 mx-auto mb-3" />
              <p className="font-mono font-black text-2xl text-amber-300 mb-1">{p.coins.toLocaleString()}</p>
              <p className="text-sm text-gray-400 mb-4">{p.name}</p>
              <button data-testid={`coin-pack-buy-button`} onClick={() => buy(p.lookup_key)} disabled={!storeOn}
                className="w-full py-2.5 rounded-xl bg-gradient-to-r from-amber-500 to-purple-600 font-semibold hover:opacity-90 transition-opacity duration-200 disabled:opacity-50 disabled:cursor-not-allowed">
                {storeOn ? `$${(p.amount / 100).toFixed(2)}` : `Coming soon · $${(p.amount / 100).toFixed(2)}`}
              </button>
            </div>
          ))}
          <p data-testid="coin-store-terms" className="sm:col-span-3 text-xs text-gray-500 text-center">{COIN_TERMS}</p>
        </div>
      )}

      {tab === "cash" && (
        <div className="space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Deposit */}
          <div className="glass rounded-2xl p-6 border border-emerald-500/20">
            <h3 className="font-display text-xl font-bold mb-4 flex items-center gap-2"><Store className="w-5 h-5 text-emerald-400" /> Add Cash</h3>
            {!user?.geo_allowed_cash ? (
              <p className="text-sm text-red-300 flex items-center gap-2"><Lock className="w-4 h-4" /> Not available in your region.</p>
            ) : (
              <div className="space-y-3">
                {catalog.cash_deposits.map((d) => (
                  <button key={d.lookup_key} data-testid="cash-deposit-trigger-button" onClick={() => buy(d.lookup_key)}
                    className="w-full flex items-center justify-between px-4 py-3 rounded-xl bg-black/30 border border-emerald-500/20 hover:border-emerald-500/50 transition-colors duration-200">
                    <span>{d.name}</span><span className="font-mono text-emerald-400">${(d.amount / 100).toFixed(2)}</span>
                  </button>
                ))}
              </div>
            )}
            {geoInfo && (
              <p data-testid="geo-detected" className="text-xs text-gray-500 mt-4 flex items-center gap-1.5">
                <MapPin className="w-3.5 h-3.5" />
                {geoInfo.detected
                  ? <>Detected location: <span className="text-gray-300">{geoInfo.region ? `${geoInfo.region}, ` : ""}{geoInfo.country || "—"}</span>{geoInfo.enforced && !geoInfo.geo_allowed_cash ? <span className="text-red-400"> · blocked</span> : null}</>
                  : <>Location could not be detected from your connection.</>}
              </p>
            )}
          </div>

          {/* KYC */}
          <div className="glass rounded-2xl p-6 border border-white/10">
            <h3 className="font-display text-xl font-bold mb-1 flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-emerald-400" /> Identity (KYC)</h3>
            <p className="text-xs text-gray-500 mb-4">Required before withdrawing cash. Status:
              <span className={`ml-1 font-semibold ${kycStatus === "approved" ? "text-emerald-400" : kycStatus === "pending" ? "text-amber-400" : kycStatus === "rejected" ? "text-red-400" : "text-gray-400"}`}>{kycStatus || "none"}</span>
            </p>
            {kycStatus === "approved" ? (
              <p className="text-emerald-300 text-sm">✅ Verified — you can withdraw.</p>
            ) : (
              <div className="space-y-3">
                <button data-testid="kyc-identity-button" onClick={verifyIdentity} disabled={verifying}
                  className="w-full py-2.5 rounded-xl bg-gradient-to-r from-emerald-500 to-cyan-600 font-semibold hover:opacity-90 transition-opacity duration-200 disabled:opacity-40 flex items-center justify-center gap-2">
                  <Fingerprint className="w-4 h-4" /> {verifying ? "Opening secure verification…" : "Verify Identity"}
                </button>
                <p className="text-xs text-gray-500">Secure ID + selfie verification powered by Stripe Identity. Your documents are handled by Stripe, not stored by us.</p>
                <button data-testid="kyc-manual-toggle" onClick={() => setShowManual((v) => !v)}
                  className="text-xs text-gray-400 hover:text-white underline underline-offset-2">
                  {showManual ? "Hide manual submission" : "Or submit documents manually"}
                </button>
                {showManual && (
                  <div className="space-y-3 pt-1 border-t border-white/5">
                    <input data-testid="kyc-name-input" placeholder="Legal full name" value={kyc.full_name} onChange={(e) => setKyc({ ...kyc, full_name: e.target.value })}
                      className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2.5 outline-none focus:border-emerald-500 transition-colors duration-200" />
                    <select value={kyc.doc_type} onChange={(e) => setKyc({ ...kyc, doc_type: e.target.value })}
                      className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2.5 outline-none text-gray-300">
                      <option value="passport">Passport</option><option value="drivers_license">Driver's License</option><option value="national_id">National ID</option>
                    </select>
                    <input data-testid="kyc-docnum-input" placeholder="Document number" value={kyc.doc_number} onChange={(e) => setKyc({ ...kyc, doc_number: e.target.value })}
                      className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2.5 outline-none focus:border-emerald-500 transition-colors duration-200" />
                    <button data-testid="kyc-submit-button" onClick={submitKyc} disabled={kycStatus === "pending" || !kyc.full_name || !kyc.doc_number}
                      className="w-full py-2.5 rounded-xl bg-emerald-500/20 border border-emerald-500/40 text-emerald-300 font-semibold hover:bg-emerald-500/30 transition-colors duration-200 disabled:opacity-40">
                      {kycStatus === "pending" ? "Under Review" : "Submit for Manual Review"}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Withdraw */}
          <div className="glass rounded-2xl p-6 border border-white/10">
            <h3 className="font-display text-xl font-bold mb-4 flex items-center gap-2"><ArrowDownToLine className="w-5 h-5 text-emerald-400" /> Withdraw</h3>
            <div className="mb-4 p-3 rounded-xl bg-black/30 border border-white/10">
              <p className="text-xs text-gray-400 mb-2">Bank payouts: {payout?.payouts_enabled
                ? <span className="text-emerald-400 font-semibold">Enabled</span>
                : <span className="text-amber-400 font-semibold">Not set up</span>}</p>
              {!payout?.payouts_enabled && (
                <button data-testid="payout-setup-button" onClick={setupPayout}
                  className="w-full py-2 rounded-lg bg-white/5 border border-emerald-500/30 text-emerald-300 text-sm hover:bg-white/10 transition-colors duration-200 flex items-center justify-center gap-2">
                  <Landmark className="w-4 h-4" /> Set up bank payouts
                </button>
              )}
            </div>
            <div className="space-y-3">
              <input data-testid="withdraw-amount-input" type="number" placeholder="Amount ($)" value={wd.amount} onChange={(e) => setWd({ ...wd, amount: e.target.value })}
                className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2.5 outline-none focus:border-emerald-500 transition-colors duration-200 font-mono" />
              <select value={wd.method} onChange={(e) => setWd({ ...wd, method: e.target.value })}
                className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2.5 outline-none text-gray-300">
                <option value="ach">ACH Bank Transfer</option><option value="giftcard">Gift Card</option>
              </select>
              <input data-testid="withdraw-dest-input" placeholder={wd.method === "ach" ? "Bank account" : "Email for gift card"} value={wd.destination} onChange={(e) => setWd({ ...wd, destination: e.target.value })}
                className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2.5 outline-none focus:border-emerald-500 transition-colors duration-200" />
              <button data-testid="withdraw-submit-button" onClick={withdraw} disabled={kycStatus !== "approved" || (smsOn && !user?.phone_verified) || !wd.amount || !wd.destination}
                className="w-full py-2.5 rounded-xl bg-gradient-to-r from-emerald-500 to-cyan-600 font-semibold hover:opacity-90 transition-opacity duration-200 disabled:opacity-40">
                Request Withdrawal
              </button>
              {kycStatus !== "approved" && <p className="text-xs text-gray-500 flex items-center gap-1"><Lock className="w-3 h-3" /> KYC approval required.</p>}
              {kycStatus === "approved" && smsOn && !user?.phone_verified && <p data-testid="withdraw-phone-required" className="text-xs text-amber-400 flex items-center gap-1"><Smartphone className="w-3 h-3" /> Verify your phone above to withdraw.</p>}
            </div>
          </div>
        </div>

        <div data-testid="payout-history" className="glass rounded-2xl p-6 border border-emerald-500/20">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display text-xl font-bold flex items-center gap-2"><History className="w-5 h-5 text-emerald-400" /> Payout History</h3>
            <span className="text-xs text-gray-500">Live · updates automatically</span>
          </div>
          {withdrawals.length === 0 ? (
            <p className="text-gray-500 text-sm">No withdrawals yet.</p>
          ) : (
            <div className="space-y-3">
              {withdrawals.map((w) => {
                const meta = WD_STATUS[w.status] || { label: w.status, pct: 50, bar: "bg-gray-500", text: "text-gray-300" };
                return (
                  <div key={w.withdrawal_id} data-testid={`withdrawal-row-${w.withdrawal_id}`} className="p-4 rounded-xl bg-black/30 border border-white/10">
                    <div className="flex items-center justify-between gap-3 mb-2">
                      <div>
                        <p className="font-mono font-bold text-emerald-300">${(w.amount || 0).toFixed(2)} <span className="text-gray-500 text-xs font-sans">via {w.method}</span></p>
                        <p className="text-xs text-gray-500">{new Date(w.created_at).toLocaleString()}</p>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${meta.text} bg-white/5 border border-white/10`}>{meta.label}</span>
                    </div>
                    <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
                      <div className={`h-full ${meta.bar} transition-all duration-500`} style={{ width: `${meta.pct}%` }} />
                    </div>
                    {w.payout_arrival_date && <p className="text-xs text-emerald-400/80 mt-2">Expected bank arrival: {new Date(w.payout_arrival_date * 1000).toLocaleDateString()}</p>}
                    {w.status === "approved" && <p className="text-xs text-gray-500 mt-2">Approved — funds are being sent.</p>}
                    {w.status === "rejected" && <p className="text-xs text-gray-500 mt-2">Rejected — the held amount was refunded to your cash balance.</p>}
                    {w.stripe_transfer_id && <p className="text-xs text-gray-600 mt-1 font-mono truncate">transfer {w.stripe_transfer_id}</p>}
                  </div>
                );
              })}
            </div>
          )}
        </div>
        </div>
      )}

      {tab === "history" && (
        <div className={`grid grid-cols-1 ${cashOn ? "lg:grid-cols-2" : ""} gap-6`}>
          <div className="glass rounded-2xl p-5 border border-amber-500/20">
            <h3 className="font-semibold mb-3 text-amber-300">Coin Ledger</h3>
            <div className="space-y-1.5 max-h-96 overflow-y-auto hide-scroll">
              {ledger.coin_ledger.map((l) => (
                <div key={l.ledger_id} className="flex justify-between text-sm py-1.5 border-b border-white/5">
                  <span className="text-gray-400 capitalize">{l.type}{l.game ? ` · ${l.game}` : ""}</span>
                  <span className={`font-mono ${l.amount >= 0 ? "text-emerald-400" : "text-red-400"}`}>{l.amount >= 0 ? "+" : ""}{l.amount.toLocaleString()}</span>
                </div>
              ))}
              {ledger.coin_ledger.length === 0 && <p className="text-gray-600 text-sm">No coin activity yet.</p>}
            </div>
          </div>
          {cashOn && <div className="glass rounded-2xl p-5 border border-emerald-500/20">
            <h3 className="font-semibold mb-3 text-emerald-300">Cash Ledger</h3>
            <div className="space-y-1.5 max-h-96 overflow-y-auto hide-scroll">
              {ledger.cash_ledger.map((l) => (
                <div key={l.ledger_id} className="flex justify-between text-sm py-1.5 border-b border-white/5">
                  <span className="text-gray-400 capitalize">{l.type}</span>
                  <span className={`font-mono ${l.amount >= 0 ? "text-emerald-400" : "text-red-400"}`}>{l.amount >= 0 ? "+$" : "-$"}{Math.abs(l.amount).toFixed(2)}</span>
                </div>
              ))}
              {ledger.cash_ledger.length === 0 && <p className="text-gray-600 text-sm">No cash activity yet.</p>}
            </div>
          </div>}
        </div>
      )}
    </div>
  );
}
